import { navigationApi } from '@ctct/infrastructure-layer';
import type { UpdateBillingPageStore } from '@ctct/state-client-layer';
import type { UpdateBillingInfoMutation, UpdateBillingInfoPayload } from '@ctct/state-server-layer';

import type { TranslationGetter } from '../../../types';
import { updateBillingEventBus } from '../UpdateBillingEventBus';

export class UpdateBillingService {
  constructor(
    private pageStore: UpdateBillingPageStore,
    private updateBillingInfoMutation: UpdateBillingInfoMutation,
    private t: TranslationGetter
  ) {}

  public api() {
    return {
      exitForm: () => this.exitForm(),
      reportBeginLoading: () => this.reportBeginLoading(),
      reportEndLoading: () => this.reportEndLoading(),
      reportSuccess: () => this.reportSuccess(),
      reportError: (error: Error) => this.reportError(error),
      getSuccessMessage: () => this.getSuccessfulUpdateMessage(),
      submitBillingChanges: (updateBillingInfoPayload: UpdateBillingInfoPayload) =>
        this.submitBillingChanges(updateBillingInfoPayload),

      reportFormDirtyState: (isDirty: boolean) => this.pageStore.setState({ updateBillingIsFormChanged: isDirty }),
      reportPaymentCanBeUpdated: (isDirty: boolean) =>
        this.pageStore.setState({ updateBillingPaymentCanBeUpdated: isDirty }),
      requestUpdateInfo: () => updateBillingEventBus.emit('requestUpdateInfo', { source: 'api' }),
    };
  }

  public data() {
    return {};
  }

  public handlers() {
    return {
      onSubmitClicked: () => updateBillingEventBus.emit('requestUpdateInfo', { source: 'user' }),
      onExitClicked: () => updateBillingEventBus.emit('requestExitForm', { source: 'user' }),
    };
  }

  private reportBeginLoading() {
    this.pageStore.setState({
      isUpdateBillingLoading: true,
      isUpdateBillingSuccess: false,
      updateBillingError: null,
    });
  }

  private reportEndLoading() {
    this.pageStore.setState({
      isUpdateBillingLoading: false,
    });
  }

  private reportSuccess() {
    this.pageStore.setState({
      isUpdateBillingLoading: false,
      isUpdateBillingSuccess: true,
    });
  }

  private reportError(error: Error) {
    this.pageStore.setState({
      isUpdateBillingLoading: false,
      isUpdateBillingSuccess: false,
      updateBillingError: error,
    });
  }

  private getSuccessfulUpdateMessage() {
    return {
      header: this.t('application-layer.billing-info-page.alert-success-heading'),
      message: this.t('application-layer.billing-info-page.alert-success-message'),
    };
  }

  private async submitBillingChanges(updateBillingInfoPayload: UpdateBillingInfoPayload) {
    await this.updateBillingInfoMutation.mutateAsync(updateBillingInfoPayload);
  }

  private exitForm() {
    navigationApi.goToSettingsBillingTab();
  }
}
